import type { NextPage } from 'next'
import { Blogs } from '@/client/modules/Blogs'
import { IPaths } from '@/client/types'
import { fetchJson, getCMSContent, getDynamicCMSContent } from '@/client/utils'

const CMSApi = process.env.NEXT_PUBLIC_API_CMS

const BlogsPage: NextPage<any> = (props) => <Blogs {...props} />

export async function getStaticPaths({ locales }) {
  const paths: IPaths[] = []

  // Fetch each locale independently so one failure
  // doesn't prevent other locales from being processed.
  const results = await Promise.allSettled(
    locales.map(async (locale) => {
      const response = await fetchJson(`${CMSApi}/blog?per_page=10&lang=${locale}`)

      return {
        locale,
        posts: Array.isArray(response) ? response : []
      }
    })
  )

  results.forEach((result) => {
    if (result.status !== 'fulfilled') {
      console.error('[Blogs] Failed to fetch posts for a locale:', result.reason)
      return
    }

    const { locale, posts } = result.value

    posts.forEach(({ slug }) => {
      // Skip posts without a valid slug.
      if (!slug || typeof slug !== 'string') {
        return
      }

      paths.push({
        params: { id: slug },
        locale
      })
    })
  })

  return {
    paths,
    fallback: 'blocking'
  }
}

// Dynamic blog page
export async function getStaticProps({ defaultLocale, params, locale }) {
  const id = params?.id

  try {
    const [data, blogsLandingPage] = await Promise.all([
      getDynamicCMSContent(locale, 'blog', id),
      getCMSContent(defaultLocale, locale, 'BLOGS_LANDING_PAGE')
    ])

    if (!data?.content || !blogsLandingPage?.content) {
      return {
        notFound: true,
        revalidate: 10
      }
    }

    const cmsResult = {
      ...data,
      blogsLandingPage: blogsLandingPage.content
    }

    return {
      revalidate: 10,
      props: cmsResult
    }
  } catch (error) {
    console.error(`[Blogs] Failed to fetch blog "${id}" for locale "${locale}"`, error)

    return {
      notFound: true,
      revalidate: 10
    }
  }
}

export default BlogsPage
